// Позиция в минусе: остаток «сейчас», тот же товар на других складах организации, остаток на конец 7 последних дней
// (график) и таблица «день — остаток — изменение».
// Телефон — лист снизу (BottomSheet). Десктоп — колонка справа от списка (StockTab ставит её в сетку): список
// сужается, а не прячется под панелью; закрытие — крестиком или Esc, другая позиция — нажатием в списке.
// Первое открытие истории организации — около 8 запросов к iikoCloud (до ~10 с), следующие товары — из кеша бэкенда.
// Под таблицей — «Используется в блюдах» (StockUsageBlock): техкарты, продажи и плашки по позиции.
import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Skeleton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BottomSheet from '../../../../components/motion/BottomSheet';
import { DESKTOP_HEADER_HEIGHT, useDesktopLayout } from '../../../../components/layout/AppLayout';
import { fetchStockHistory } from '../../../../services/dataService';
import { CARD_LABEL_COLOR, CARD_SURFACE_SX, DIVIDER_COLOR, MUTED_TEXT_COLOR } from '../../../../styles/tokens';
import { STALE_COLOR, plural } from '../../../Kitchen/kitchenFormat';
import { dayLabel, formatChange, formatQuantity } from '../../utils/stockModel';
import StockHistoryChart, { NEGATIVE_COLOR, POSITIVE_COLOR } from './StockHistoryChart';
import StockUsageBlock from './StockUsageBlock';
import { useStockRequest } from './useStockRequest';

const DAYS = 7;
export const PANEL_WIDTH = { md: 360, lg: 420 };
// Колонка закреплена при прокрутке под закреплённой шапкой (она поверх содержимого) с отступом;
// по высоте — окно без шапки и отступов сверху и снизу
const STICKY_GAP = 16;
const STICKY_TOP = DESKTOP_HEADER_HEIGHT + STICKY_GAP;

function HistoryTable({ days, unit, wentNegative }) {
  const cell = { fontSize: '14px', fontVariantNumeric: 'tabular-nums', textAlign: 'right' };
  return (
    <Box role="table" aria-label="Остаток по дням">
      <Box role="row" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, py: 0.75, borderBottom: `1px solid ${DIVIDER_COLOR}` }}>
        {['День', 'Остаток', 'Изменение'].map((title, index) => (
          <Typography key={title} role="columnheader" sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR, textAlign: index ? 'right' : 'left' }}>{title}</Typography>
        ))}
      </Box>
      {/* Свежие дни сверху */}
      {[...days].reverse().map((day) => {
        const went = day.date === wentNegative;
        return (
          <Box key={day.date} role="row" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, alignItems: 'center', minHeight: 36, borderBottom: `1px solid ${DIVIDER_COLOR}`, '&:last-of-type': { borderBottom: 'none' } }}>
            <Typography role="cell" sx={{ fontSize: '14px', color: went ? NEGATIVE_COLOR : CARD_LABEL_COLOR, fontWeight: went ? 700 : 400 }}>
              {dayLabel(day.date)}
              {went && ' · в минус'}
            </Typography>
            <Typography role="cell" sx={{ ...cell, color: day.balance < 0 ? NEGATIVE_COLOR : '#fff' }}>{formatQuantity(day.balance, unit)}</Typography>
            <Typography role="cell" sx={{ ...cell, color: CARD_LABEL_COLOR }}>{formatChange(day.change, unit)}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// Тот же товар на остальных складах организации (из снимка «сейчас»): плюс — зелёным, минус — красным.
// Нет — серая строка: у организации один склад или на других этого товара нет
function OtherStores({ stores, storeCount, unit }) {
  if (!stores) return null; // история ещё грузится
  if (stores.length === 0) {
    return (
      <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR, mt: -1 }}>
        {storeCount > 1 ? 'На других складах организации этого товара нет' : 'Других складов в организации нет'}
      </Typography>
    );
  }
  return (
    <Box component="section" aria-label="На других складах">
      <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#fff', mb: 0.5 }}>На других складах</Typography>
      <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {stores.map((store, index) => (
          <Box
            component="li"
            key={store.store_id}
            sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, py: 0.75, borderTop: index ? `1px solid ${DIVIDER_COLOR}` : 'none' }}
          >
            <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR, minWidth: 0, overflowWrap: 'anywhere' }}>{store.name}</Typography>
            <Typography sx={{ flexShrink: 0, fontSize: '14px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: store.quantity < 0 ? NEGATIVE_COLOR : POSITIVE_COLOR }}>
              {formatQuantity(store.quantity, unit)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function PanelContent({ item, onClose, titleRef }) {
  const { data, failed } = useStockRequest(item, fetchStockHistory, DAYS);
  const unit = data?.unit ?? item.unit;
  let history;
  if (failed || data?.available === false) {
    history = <Typography sx={{ color: CARD_LABEL_COLOR }}>Не удалось загрузить остатки по дням — попробуйте позже.</Typography>;
  } else if (!data) {
    history = (
      <Box aria-busy="true" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>Собираем остатки по дням — в первый раз до 10 секунд</Typography>
        <Skeleton variant="rounded" height={160} />
        {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
      </Box>
    );
  } else {
    history = (
      <>
        <StockHistoryChart days={data.days} wentNegative={data.went_negative} unit={unit} />
        {(data.went_negative || data.negative_whole_period) && (
          <Typography sx={{ fontSize: '13px', color: NEGATIVE_COLOR }}>
            {data.went_negative
              ? `Ушёл в минус ${dayLabel(data.went_negative)}`
              : `В минусе все ${data.days.length} ${plural(data.days.length, ['день', 'дня', 'дней'])}`}
          </Typography>
        )}
        <HistoryTable days={data.days} unit={unit} wentNegative={data.went_negative} />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            ref={titleRef}
            tabIndex={-1}
            id="stock-item-title"
            component="h2"
            sx={{ fontSize: '18px', fontWeight: 600, color: '#fff', overflowWrap: 'anywhere', outline: 'none' }}
          >
            {item.name}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>
            {[item.storeName, item.groupName].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Закрыть" sx={{ color: '#e4e4e7', mr: -1, mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '14px', color: CARD_LABEL_COLOR }}>Сейчас</Typography>
        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: (data?.current ?? item.quantity) < 0 ? NEGATIVE_COLOR : '#fff', fontVariantNumeric: 'tabular-nums' }}>
          {formatQuantity(data?.current ?? item.quantity, unit)}
        </Typography>
      </Box>
      {data?.stale && (
        <Typography role="status" sx={{ fontSize: '13px', color: STALE_COLOR }}>iikoCloud сейчас недоступен — данные последнего снимка</Typography>
      )}
      <OtherStores stores={data?.other_stores} storeCount={data?.store_count} unit={unit} />

      <Box component="section" aria-label="Остаток на конец дня" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>Остаток на конец дня, {DAYS} дней</Typography>
        {history}
      </Box>

      {/* Своё состояние «Показать все» у каждой позиции — новый экземпляр на позицию */}
      <StockUsageBlock key={`${item.storeId}:${item.product_id}`} item={item} days={DAYS} />
    </Box>
  );
}

// Десктоп: колонка справа от списка. Фокус — на название при открытии и смене позиции; Esc закрывает
function DesktopPanel({ item, onClose }) {
  const titleRef = useRef(null);
  useEffect(() => {
    titleRef.current?.focus({ preventScroll: true });
  }, [item]);
  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Box
      component="aside"
      role="region"
      aria-labelledby="stock-item-title"
      sx={{
        ...CARD_SURFACE_SX,
        position: 'sticky',
        top: STICKY_TOP,
        maxHeight: `calc(100vh - ${STICKY_TOP + STICKY_GAP}px)`,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        boxSizing: 'border-box',
        p: 2.5,
        color: '#fff',
      }}
    >
      <PanelContent item={item} onClose={onClose} titleRef={titleRef} />
    </Box>
  );
}

// item — позиция списка с организацией и складом: { product_id, name, unit, quantity, organizationId, storeId, storeName, groupName }.
// На десктопе рисуется только открытая (StockTab ставит колонку в сетку), на телефоне — лист снизу
export default function StockItemPanel({ item, onClose }) {
  const desktop = useDesktopLayout();
  // Содержимое листа держим, пока он уезжает: после закрытия item уже null
  const [shown, setShown] = useState(item);
  useEffect(() => {
    if (item) setShown(item);
  }, [item]);

  if (desktop) return item ? <DesktopPanel item={item} onClose={onClose} /> : null;
  return (
    <BottomSheet
      open={Boolean(item)}
      onClose={onClose}
      onExited={() => setShown(null)}
      labelledBy="stock-item-title"
      paperSx={{ ...CARD_SURFACE_SX, borderRadius: '16px 16px 0 0', maxHeight: 'calc(100% - 24px)' }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', px: 3, pb: 3 }}>
        {shown ? <PanelContent item={shown} onClose={onClose} /> : null}
      </Box>
    </BottomSheet>
  );
}
